import { Box, Checkbox, Divider, Grid } from "@mui/material";
import { Form, Formik, FormikErrors, FormikValues } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import FormStepBottomComponent from "../../../common/form/Steps/FormStepBottomComponent";
import FormStepNavComponent from "../../../common/form/Steps/FormStepComponent";
import StepsHeadingSection from "../../../common/form/Steps/StepsHeadingSection";
import ConfirmationModal from "../../../common/modal/confirmationModal/ConfirmationModal";

import { useAppDispatch } from "../../../../redux/store";
import { setLoadingAction } from "../../../../redux/utils/actions";
import departmentService from "../../../../services/department";
import { logger } from "../../../../utils/logger";
import RoutingList from "../../../../utils/RoutingList";
import {
  departmentCreateInitData,
  departmentCreationStepsInfo,
  departmentCreationValidationSchema,
  departmentEditStepsInfo,
} from "../utils";
import styles from "./../styles.module.scss";
import AddDepartmentForm from "./AddDepartmentForm";

const AddDepartment = () => {
  const [initData] = useState(departmentCreateInitData);
  const [stepsInfo, setStepsInfo] = useState(departmentCreationStepsInfo);
  const [steps, setSteps] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams();

  const stepHandler = (
    validateForm: (values: FormikValues) => Promise<any>,
    values: FormikValues,
    stepIndex: number,
    setErrors?: (errors: FormikErrors<FormikValues>) => void
  ) => {
    validateForm(values).then((res) => {
      if (Object.entries(res).length === 0) {
        // validation success
        const lastStepIndex = stepsInfo?.length - 1;

        if (stepIndex <= -1) {
          setSteps(0);
          return;
        } else if (steps === lastStepIndex && stepIndex > lastStepIndex) {
          setShowConfirmationModal(true);
          return;
        } else if (stepIndex <= lastStepIndex) {
          setSteps((prev) => stepIndex);
          return;
        }
      } else {
        // validation failed
        logger.error(res);
        if (setErrors) {
          setErrors(res);
        }
      }
    });
  };

  const saveHandler = async (values: FormikValues) => {
    try {
      const validValues = { ...values };

      delete validValues["confirmPassword"];

      for (const key of Object.keys(validValues)) {
        if (!validValues[key]) {
          delete validValues[key];
        }
      }
      dispatch(setLoadingAction(true));
      if (id) {
        await departmentService.update(id, {
          name: values.name,
          expiresOn: values.expiresOn,
          expiresIn: values.expiresIn,
          maxAmount: values.maxAmount,
        });
      } else {
        await departmentService.create({
          name: values.name,
          expiresOn: values.expiresOn,
          expiresIn: values.expiresIn,
          maxAmount: values.maxAmount,
        });
      }
      dispatch(setLoadingAction(false));
      setShowConfirmationModal(false);
      navigate(RoutingList?.department?.index);
      toast.success("Department created successfully");
    } catch (err: any) {
      dispatch(setLoadingAction(false));
      setShowConfirmationModal(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    if (id) {
      setStepsInfo(departmentEditStepsInfo);
    }
  }, [id]);

  return (
    <>
      <Box className={styles.boxPadding}>
        <Formik
          enableReinitialize={true}
          validationSchema={departmentCreationValidationSchema[steps]}
          initialValues={initData}
          // validateOnChange={false}
          onSubmit={(
            values,
            {
              setSubmitting,
              validateForm,
              setFieldError,
              setErrors,
              setTouched,
            }
          ) => {
            stepHandler(
              validateForm,
              values,
              steps > stepsInfo?.length - 1 ? steps : steps + 1,
              setErrors
            );
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldTouched,
            setFieldValue,
          }) => (
            <Form>
              <StepsHeadingSection
                title="Department"
                subtitle="Department"
                isEdit={!!id}
              />
              <FormStepNavComponent
                steps={steps}
                setSteps={setSteps}
                stepHandler={stepHandler}
                stepsInfo={stepsInfo}
              />
              <Grid container sx={{ justifyContent: "center" }}>
                <Grid item xs={12} lg={8}>
                  <AddDepartmentForm steps={steps} />
                  <Divider className={styles.mTopBottom20} />
                  <ConfirmationModal
                    open={showConfirmationModal}
                    onConfirm={() => saveHandler(values)}
                    onClose={() => {
                      setShowConfirmationModal(false);
                    }}
                  />{" "}
                  <FormStepBottomComponent
                    steps={steps}
                    setSteps={setSteps}
                    stepsInfo={stepsInfo}
                    stepHandler={stepHandler}
                  />
                </Grid>
              </Grid>{" "}
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default AddDepartment;
